import { useCallback, useEffect, useMemo, useState } from "react";
import StudioSponsorshipSection from "../../../components/midpoint/clientViews/studio/StudioSponsorshipSection";
import {
  createAutomapPartnerSponsorshipApi,
  fetchAutomapSponsorshipSettings,
  patchAutomapSponsorshipSettings,
} from "../../../api/sponsorshipSectionApi";
import "../../../styles/clientViewStudio.css";

export default function AutoMapaCampaignsPage() {
  const sponsorshipApi = useMemo(() => createAutomapPartnerSponsorshipApi(), []);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [workspaceKey, setWorkspaceKey] = useState(0);

  const loadSettings = useCallback(async () => {
    setError("");
    try {
      const body = await fetchAutomapSponsorshipSettings();
      setSettings(body);
    } catch (err) {
      setSettings(null);
      if (err.status === 403) {
        setError(
          "Your partner account does not have AutoMapa access. Sign in with an AutoMapa operator account at the admin login page."
        );
      } else if (err.status === 404) {
        setError(
          "Campaigns API is unavailable. Restart the API server so the latest AutoMapa partner routes are loaded."
        );
      } else {
        setError(err.message || "Unable to load campaign settings.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleToggleCapability = async () => {
    if (!settings) return;
    const nextEnabled = !settings.sponsorshipEnabled;
    setSaving(true);
    setError("");
    try {
      const body = await patchAutomapSponsorshipSettings(nextEnabled);
      await loadSettings();
      if (nextEnabled || body.seededSlotCount > 0) {
        setWorkspaceKey((current) => current + 1);
      }
    } catch (err) {
      setError(err.message || "Unable to update campaigns capability.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="muted panel-card">Loading campaigns…</p>;
  }

  return (
    <div className="panel-card automap-sponsorship-admin">
      <header className="automap-sponsorship-admin__header">
        <div>
          <h2>Campaigns</h2>
          <p className="muted">
            Enable sponsored campaigns on AutoMapa, group placements for billing, and track
            performance across partner accounts.
          </p>
        </div>
        <div className="automap-sponsorship-admin__capability">
          <div>
            <strong>Campaigns capability</strong>
            <p className="muted">
              {settings?.sponsorshipEnabled
                ? "Live on the published AutoMapa client view."
                : "Disabled — campaigns and sponsored placements will not run on the public site."}
            </p>
          </div>
          <button
            type="button"
            className={`automap-sponsorship-admin__toggle${settings?.sponsorshipEnabled ? " is-on" : ""}`}
            disabled={saving || !settings}
            onClick={handleToggleCapability}
          >
            {saving ? "Saving…" : settings?.sponsorshipEnabled ? "Enabled" : "Enable campaigns"}
          </button>
        </div>
      </header>

      {error ? <p className="automap-sponsorship-admin__error">{error}</p> : null}

      {!settings?.sponsorshipEnabled ? (
        <div className="automap-sponsorship-admin__empty panel-card">
          <h3>Turn on campaigns to get started</h3>
          <p className="muted">
            Enabling campaigns publishes the sponsorship feature on AutoMapa and seeds the standard
            inventory slots used for sponsored listings. You can then create campaigns and link them
            to placements.
          </p>
          <ul className="cv-studio-list">
            {(settings?.defaultSlots || []).map((slot) => (
              <li key={slot.slotKey}>
                <code>{slot.slotKey}</code> — {slot.name}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <StudioSponsorshipSection
          key={workspaceKey}
          clientViewId={settings.clientViewId}
          canManage
          api={sponsorshipApi}
          initialSubTab="campaigns"
        />
      )}
    </div>
  );
}
